import BookmarkAddIcon from "@mui/icons-material/BookmarkAdd";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNotification } from "../../contexts/NotificationContext";
import {
  CreatePortfolioItemRequest,
  CreatePortfolioItemResponse,
  Portfolio,
  portfolioApi,
} from "../../services/portfolioApi";

interface AddToPortfolioButtonProps {
  entityType: "organization" | "facility" | "project" | "source";
  entityId: string;
  entityName?: string;
  variant?: "button" | "icon";
  size?: "small" | "medium" | "large";
  onSuccess?: () => void;
}

const AddToPortfolioButton: React.FC<AddToPortfolioButtonProps> = ({
  entityType,
  entityId,
  entityName,
  variant = "button",
  size = "medium",
  onSuccess,
}) => {
  const { showNotification } = useNotification();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [labels, setLabels] = useState<string[]>([]);
  const [labelInput, setLabelInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newPortfolioName, setNewPortfolioName] = useState("");
  const [newPortfolioDescription, setNewPortfolioDescription] = useState("");

  useEffect(() => {
    if (dialogOpen) {
      loadPortfolios();
    }
  }, [dialogOpen]);

  const loadPortfolios = async () => {
    try {
      const response = await portfolioApi.getPortfolios();
      // Filter portfolios where user has OWNER or EDITOR permissions
      const editablePortfolios = response.results.filter(
        (p) => p.user_permission === "OWNER" || p.user_permission === "EDITOR"
      );
      setPortfolios(editablePortfolios);
    } catch (error) {
      return;
    }
  };

  const handleAddToPortfolio = async () => {
    if (!selectedPortfolio) return;

    try {
      setLoading(true);
      const itemData: CreatePortfolioItemRequest = {
        entity_type: entityType,
        entity_id: entityId,
        notes: notes.trim() || undefined,
        labels: labels.length > 0 ? labels : undefined,
      };

      const response: CreatePortfolioItemResponse =
        await portfolioApi.createPortfolioItem(selectedPortfolio, itemData);
      const portfolioName =
        portfolios.find((p) => p.id === selectedPortfolio)?.name || "portfolio";

      // Show appropriate notification based on response status
      if (response.status === "created") {
        showNotification(
          `Successfully added ${entityName || "item"} to ${portfolioName}`,
          "success"
        );
      } else if (response.status === "already_exists") {
        showNotification(
          `${entityName || "Item"} is already in ${portfolioName}`,
          "info"
        );
      }

      // Reset form
      resetForm();
      setDialogOpen(false);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      showNotification(
        "Failed to add item to portfolio. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAddLabel = () => {
    if (labelInput.trim() && !labels.includes(labelInput.trim())) {
      setLabels([...labels, labelInput.trim()]);
      setLabelInput("");
    }
  };

  const handleRemoveLabel = (labelToRemove: string) => {
    setLabels(labels.filter((label) => label !== labelToRemove));
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleAddLabel();
    }
  };

  const handleCreatePortfolio = async () => {
    if (!newPortfolioName.trim()) return;

    try {
      setLoading(true);
      const newPortfolio = await portfolioApi.createPortfolio({
        name: newPortfolioName.trim(),
        description: newPortfolioDescription.trim() || undefined,
      });

      // Refresh portfolios list
      await loadPortfolios();

      // Select the newly created portfolio
      setSelectedPortfolio(newPortfolio.id);

      // Reset create form
      setNewPortfolioName("");
      setNewPortfolioDescription("");
      setShowCreateForm(false);

      showNotification(
        `Portfolio "${newPortfolio.name}" created successfully`,
        "success"
      );
    } catch (error) {
      showNotification(
        "Failed to create portfolio. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedPortfolio("");
    setNotes("");
    setLabels([]);
    setLabelInput("");
    setShowCreateForm(false);
    setNewPortfolioName("");
    setNewPortfolioDescription("");
  };

  const renderButton = () => {
    if (variant === "icon") {
      return (
        <Tooltip title="Add to Portfolio">
          <IconButton
            size={size}
            onClick={() => setDialogOpen(true)}
            sx={{ color: "primary.main" }}
          >
            <BookmarkAddIcon />
          </IconButton>
        </Tooltip>
      );
    }

    return (
      <Button
        variant="outlined"
        size={size}
        startIcon={<BookmarkAddIcon />}
        onClick={() => setDialogOpen(true)}
        sx={{ minWidth: "auto" }}
      >
        Add to Portfolio
      </Button>
    );
  };

  return (
    <>
      {renderButton()}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={false}
        PaperProps={{
          onClick: (e: React.MouseEvent) => e.stopPropagation(),
          sx: { zIndex: 1300, backgroundColor: "#1e1e1e" },
        }}
      >
        <DialogTitle sx={{ backgroundColor: "#1e1e1e" }}>
          Add to Portfolio
          {entityName && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {entityName} ({entityType})
            </Typography>
          )}
        </DialogTitle>

        <DialogContent sx={{ backgroundColor: "#1e1e1e" }}>
          <FormControl fullWidth margin="normal">
            <InputLabel>Select Portfolio</InputLabel>
            <Select
              value={selectedPortfolio}
              onChange={(e: any) => {
                e.stopPropagation();
                setSelectedPortfolio(e.target.value);
              }}
              label="Select Portfolio"
              MenuProps={{
                PaperProps: {
                  sx: { zIndex: 1400 },
                },
                onClick: (e: React.MouseEvent) => e.stopPropagation(),
              }}
              disabled={showCreateForm}
            >
              {portfolios.map((portfolio) => (
                <MenuItem
                  key={portfolio.id}
                  value={portfolio.id}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                  <Box>
                    <Typography variant="body1">{portfolio.name}</Typography>
                    {portfolio.description && (
                      <Typography variant="body2" color="text.secondary">
                        {portfolio.description}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Create New Portfolio Section */}
          <Box sx={{ mt: 1, mb: 2 }}>
            {!showCreateForm ? (
              <Button
                variant="text"
                size="small"
                startIcon={<BookmarkAddIcon />}
                onClick={() => setShowCreateForm(true)}
                sx={{
                  textTransform: "none",
                  color: "primary.main",
                  "&:hover": {
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                  },
                }}
              >
                Create New Portfolio
              </Button>
            ) : (
              <Box
                sx={{
                  p: 2,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  backgroundColor: "#1e1e1e",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 2, color: "primary.main" }}
                >
                  Create New Portfolio
                </Typography>

                <TextField
                  fullWidth
                  label="Portfolio Name"
                  value={newPortfolioName}
                  onChange={(e) => setNewPortfolioName(e.target.value)}
                  margin="dense"
                  required
                  placeholder="Enter portfolio name..."
                />

                <TextField
                  fullWidth
                  label="Description (optional)"
                  value={newPortfolioDescription}
                  onChange={(e) => setNewPortfolioDescription(e.target.value)}
                  margin="dense"
                  multiline
                  rows={2}
                  placeholder="Brief description of this portfolio..."
                />

                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    gap: 1,
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    size="small"
                    onClick={() => {
                      setShowCreateForm(false);
                      setNewPortfolioName("");
                      setNewPortfolioDescription("");
                    }}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleCreatePortfolio}
                    disabled={!newPortfolioName.trim() || loading}
                  >
                    {loading ? "Creating..." : "Create"}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>

          <TextField
            fullWidth
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            margin="normal"
            multiline
            rows={3}
            placeholder="Add any notes about this item..."
          />

          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Add Labels"
              value={labelInput}
              onChange={(e) => setLabelInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a label and press Enter"
              InputProps={{
                endAdornment: labelInput.trim() && (
                  <Button onClick={handleAddLabel} size="small">
                    Add
                  </Button>
                ),
              }}
            />

            {labels.length > 0 && (
              <Box sx={{ mt: 1, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {labels.map((label, index) => (
                  <Chip
                    key={index}
                    label={label}
                    onDelete={() => handleRemoveLabel(label)}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ backgroundColor: "#1e1e1e" }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddToPortfolio}
            variant="contained"
            disabled={!selectedPortfolio || loading}
          >
            {loading ? "Adding..." : "Add to Portfolio"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddToPortfolioButton;
